import type { AuthenticatorsConfig, UserAPI } from '@sydle/ui-api';
import { getAPI } from '@sydle/ui-api';
import type { UIAppConfig } from '@sydle/ui-app';
import { getConfig } from '@sydle/ui-utils';
import type { AvailableSocialNetworks } from '../components/social-button/social-button-options';
import type { CatalogNode } from '../models/CatalogNode';
import type { LoginLink } from '../models/LoginLink';
import type { ServiceDeskConfig } from '../models/serviceDeskConfig';
import { syQuerySelector } from './dom.utils';

export function getInitials(name: string): string | undefined {
  if (name) {
    const fullName = name.replace(/\s(de|da|dos|das)\s/g, ' '); // Remove 'de', 'da', 'dos', 'das'.
    const allInitials = fullName.match(/\b(\w)/gi);
    return allInitials?.[0].concat(allInitials[allInitials.length - 1]) ?? undefined; // Initials from first and last name
  }
}

export function sySdScrollTo(ref: Element): void {
  if (!ref) {
    return;
  }

  const navbar = syQuerySelector('sy-sd-navbar');
  const navbarOffset = navbar ? navbar.getBoundingClientRect().height : 0;

  // Offset so the element isn't so close to the navbar
  const breathingOffset = -15;
  const y = ref.getBoundingClientRect().top + window.pageYOffset - navbarOffset + breathingOffset;
  window.scrollTo({ top: y, behavior: 'smooth' });
}

export function generateId(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0,
      v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

export function isId(id: string): boolean {
  return /^[a-z0-9]{24}$/.test(id);
}

export function getPathIdentifiers(node: CatalogNode | null, endIdx?: number): string {
  if (!node?.path?.length || !(typeof node.path === 'object') || node.path[0]._classIdentifier == 'homePageSection') {
    return '';
  }

  const identifiers = node.path.map((el: CatalogNode) => {
    return el.identifier || '';
  });

  if ((endIdx || endIdx === 0) && endIdx > -1) {
    const slicedIdentifiers = identifiers.slice(0, endIdx + 1);
    return `${slicedIdentifiers.join('+')}`;
  }

  return `${identifiers.join('+').replace(/\./g, '-')}`;
}

export function getTitleSufix(): string | undefined {
  const uiAppConfig = getConfig<UIAppConfig>('ui-app');
  const userAPI = getAPI<UserAPI>('userAPI');

  return uiAppConfig?.['titleSufix__i18n']?.[userAPI?.getCurrentLanguage() ?? ''] ?? uiAppConfig?.titleSufix;
}

export function generateLoginLink(portalConfig: ServiceDeskConfig | null, authConfig: AuthenticatorsConfig): LoginLink {
  const authData = portalConfig?.auth || {};

  const link: LoginLink = {
    name: authData[authConfig.identifier]?.name || authConfig.name,
    identifier: authConfig.identifier,
    description: authData[authConfig.identifier]?.description || '',
    style: authData[authConfig.identifier]?.style || {},
  };

  if (authConfig.type === 'certificate') {
    link.type = authConfig.type;
  } else {
    link.loginUrl = authConfig.loginUrl + '?one_redirect=' + encodeURIComponent(window.location.href);
  }

  return link;
}

export function matchCustomSocialIdentifier(id: string): AvailableSocialNetworks | boolean {
  return (
    (id?.toLowerCase().includes('google') && 'google') ||
    (id?.toLowerCase().includes('facebook') && 'facebook') ||
    (id?.toLowerCase().includes('linkedin') && 'linkedin') ||
    (id?.toLowerCase().includes('gov') && 'gov') ||
    false
  );
}
